import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';
import VisionAireLogo from '@/components/VisionAireLogo';
import API, { formatApiError } from '@/lib/api';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            await API.post('/auth/reset-password', { token, new_password: password });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
                <div className="text-center">
                    <VisionAireLogo size={56} className="mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-zinc-900 mb-2">Invalid Reset Link</h1>
                    <p className="text-sm text-zinc-500 mb-4">This link is missing or invalid. Please request a new password reset.</p>
                    <Link to="/forgot-password">
                        <Button className="bg-amber-500 hover:bg-amber-600 text-zinc-900 rounded-xl" data-testid="request-new-reset-btn">
                            Request New Reset
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <VisionAireLogo size={56} />
                    </div>
                    <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Set New Password</h1>
                    <p className="text-sm text-zinc-500 mt-1">Enter your new password below</p>
                </div>

                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
                    {success ? (
                        <div className="text-center py-4" data-testid="reset-success">
                            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                            <h2 className="text-lg font-semibold text-zinc-900 mb-2">Password Reset!</h2>
                            <p className="text-sm text-zinc-600 mb-2">Your password has been updated. Redirecting to login...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="reset-error">{error}</div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-zinc-700 mb-1.5">New Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Min 6 characters"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="pl-10 pr-10 rounded-xl"
                                        data-testid="reset-password-input"
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Confirm Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Confirm your new password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        className="pl-10 rounded-xl"
                                        data-testid="reset-confirm-input"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                disabled={loading || !password || !confirm}
                                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold rounded-xl h-11"
                                data-testid="reset-submit-btn"
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
